import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Badge } from '@/components/ui/badge'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog'
import { Textarea } from '@/components/ui/textarea'
import { fetchSupportTickets, createSupportTicket, SupportTicket } from '@/api/support'
import { Ticket, Plus, Clock, CheckCircle, AlertCircle, MessageSquare } from 'lucide-react'
import { cn, formatDate, getStatusColor } from '@/lib/utils'
import { toast } from 'sonner'

export function Support() {
  const [isDialogOpen, setIsDialogOpen] = useState(false)
  const queryClient = useQueryClient()
  
  const { data, isLoading } = useQuery({
    queryKey: ['support-tickets'],
    queryFn: fetchSupportTickets,
  })
  
  const createMutation = useMutation({
    mutationFn: createSupportTicket,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['support-tickets'] })
      setIsDialogOpen(false)
      toast.success('Support ticket created successfully')
    },
    onError: () => {
      toast.error('Failed to create support ticket')
    }
  })

  if (isLoading) {
    return (
      <div className="space-y-6">
        <div className="h-32 animate-pulse rounded-lg bg-muted" />
        <div className="space-y-4">
          {[1, 2, 3].map((i) => (
            <div key={i} className="h-24 animate-pulse rounded-lg bg-muted" />
          ))}
        </div>
      </div>
    )
  }

  const tickets = data?.tickets || []
  const openTickets = tickets.filter((t: SupportTicket) => t.status !== 'closed')
  const closedTickets = tickets.filter((t: SupportTicket) => t.status === 'closed')

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Support Center</h1>
          <p className="text-muted-foreground">
            View and manage your support tickets
          </p>
        </div>
        <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
          <DialogTrigger asChild>
            <Button>
              <Plus className="mr-2 h-4 w-4" />
              New Ticket
            </Button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-[500px]">
            <NewTicketForm onSubmit={createMutation.mutate} isPending={createMutation.isPending} />
          </DialogContent>
        </Dialog>
      </div>

      {/* Stats */}
      <div className="grid gap-4 md:grid-cols-3">
        <Card>
          <CardContent className="flex items-center gap-4 p-6">
            <div className="flex h-12 w-12 items-center justify-center rounded-full bg-yellow-500/10">
              <Ticket className="h-6 w-6 text-yellow-500" />
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Open Tickets</p>
              <p className="text-2xl font-bold">{openTickets.length}</p>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="flex items-center gap-4 p-6">
            <div className="flex h-12 w-12 items-center justify-center rounded-full bg-green-500/10">
              <CheckCircle className="h-6 w-6 text-green-500" />
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Resolved</p>
              <p className="text-2xl font-bold">{closedTickets.length}</p>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="flex items-center gap-4 p-6">
            <div className="flex h-12 w-12 items-center justify-center rounded-full bg-cloudflare-orange/10">
              <MessageSquare className="h-6 w-6 text-cloudflare-orange" />
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Chat Escalations</p>
              <p className="text-2xl font-bold">-</p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Tickets List */}
      <Tabs defaultValue="open">
        <TabsList>
          <TabsTrigger value="open">
            Open ({openTickets.length})
          </TabsTrigger>
          <TabsTrigger value="closed">
            Closed ({closedTickets.length})
          </TabsTrigger>
        </TabsList>
        
        <TabsContent value="open" className="space-y-4">
          {openTickets.length === 0 ? (
            <Card>
              <CardContent className="flex flex-col items-center justify-center p-12 text-center">
                <CheckCircle className="h-12 w-12 text-green-500" />
                <h3 className="mt-4 text-lg font-medium">All caught up!</h3>
                <p className="mt-2 text-sm text-muted-foreground">
                  You have no open support tickets.
                </p>
              </CardContent>
            </Card>
          ) : (
            openTickets.map((ticket: SupportTicket) => (
              <TicketCard key={ticket.id} ticket={ticket} />
            ))
          )}
        </TabsContent>
        
        <TabsContent value="closed" className="space-y-4">
          {closedTickets.map((ticket: SupportTicket) => (
            <TicketCard key={ticket.id} ticket={ticket} />
          ))}
        </TabsContent>
      </Tabs>
    </div>
  )
}

function TicketCard({ ticket }: { ticket: SupportTicket }) {
  const statusColors: Record<string, string> = {
    open: 'bg-yellow-500',
    in_progress: 'bg-blue-500',
    closed: 'bg-green-500'
  }
  
  const priorityColors: Record<string, string> = {
    low: 'bg-blue-500',
    medium: 'bg-yellow-500',
    high: 'bg-orange-500',
    urgent: 'bg-red-500'
  }

  return (
    <Card>
      <CardContent className="flex items-center gap-4 p-4">
        <div className={cn(
          'flex h-10 w-10 items-center justify-center rounded-full',
          statusColors[ticket.status]
        )}>
          {ticket.status === 'closed' ? (
            <CheckCircle className="h-5 w-5 text-white" />
          ) : ticket.status === 'in_progress' ? (
            <Clock className="h-5 w-5 text-white" />
          ) : (
            <AlertCircle className="h-5 w-5 text-white" />
          )}
        </div>
        <div className="flex-1 min-w-0">
          <h3 className="truncate font-medium">{ticket.subject}</h3>
          <p className="text-sm text-muted-foreground line-clamp-1">
            {ticket.description}
          </p>
          <p className="mt-1 text-xs text-muted-foreground">
            Created {formatDate(ticket.created_at)}
          </p>
        </div>
        <div className="flex flex-col items-end gap-2">
          <Badge className={cn(priorityColors[ticket.priority], 'text-white')}>
            {ticket.priority}
          </Badge>
          <Badge variant="outline">{ticket.status.replace('_', ' ')}</Badge>
        </div>
      </CardContent>
    </Card>
  )
}

function NewTicketForm({ onSubmit, isPending }: { onSubmit: (data: any) => void, isPending: boolean }) {
  const [subject, setSubject] = useState('')
  const [description, setDescription] = useState('')
  const [priority, setPriority] = useState('medium')

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    onSubmit({ subject, description, priority })
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <DialogHeader>
        <DialogTitle>Create Support Ticket</DialogTitle>
      </DialogHeader>
      <div className="space-y-4">
        <div>
          <label className="text-sm font-medium">Subject</label>
          <Input 
            value={subject} 
            onChange={(e) => setSubject(e.target.value)} 
            placeholder="Brief description of your issue"
            required
          />
        </div>
        <div>
          <label className="text-sm font-medium">Description</label>
          <Textarea 
            value={description} 
            onChange={(e) => setDescription(e.target.value)} 
            placeholder="Detailed description of your issue"
            rows={4}
            required
          />
        </div>
        <div>
          <label className="text-sm font-medium">Priority</label>
          <select 
            value={priority} 
            onChange={(e) => setPriority(e.target.value)}
            className="w-full rounded-md border border-input bg-background px-3 py-2"
          >
            <option value="low">Low</option>
            <option value="medium">Medium</option>
            <option value="high">High</option>
            <option value="urgent">Urgent</option>
          </select>
        </div>
      </div>
      <Button type="submit" className="w-full" disabled={isPending}>
        {isPending ? 'Creating...' : 'Create Ticket'}
      </Button>
    </form>
  )
}
